import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, ClickAwayListener, Tooltip, Typography } from "@mui/material";
import {
  formatCell,
  getCellColor,
  identityFormatLabel,
} from "components/labellerAccuracy/confusionMatrixUtils";
import { useTenantTranslation } from "hooks/formatters";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  use,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import { ConfusionMatrixEntry } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";

interface PinnedTooltipContextValue {
  pinnedId: string | null;
  setPinnedId: Dispatch<SetStateAction<string | null>>;
}

const PinnedTooltipContext = createContext<PinnedTooltipContextValue | null>(
  null
);

const usePinnedTooltip = (): PinnedTooltipContextValue => {
  const ctx = use(PinnedTooltipContext);
  if (ctx === null) {
    throw new Error(
      "AccuracyTooltip must be used within a PinnedTooltipProvider"
    );
  }
  return ctx;
};

export const PinnedTooltipProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const value = useMemo(() => ({ pinnedId, setPinnedId }), [pinnedId]);
  return <PinnedTooltipContext value={value}>{children}</PinnedTooltipContext>;
};

interface MatchAccuracyProps {
  type: "match";
  matchedEvents: number;
  totalLabellerEvents: number;
  totalVerifiedEvents: number;
  children: ReactNode;
}

interface ConfusionMatrixProps {
  type: "confusion_matrix";
  entries: ConfusionMatrixEntry[];
  formatLabel?: (label: string) => string;
  buildCellHref: (predicted: string, actual: string) => string;
  children: ReactNode;
}

type Props = MatchAccuracyProps | ConfusionMatrixProps;

const MatchAccuracyContent = ({
  matchedEvents,
  totalLabellerEvents,
  totalVerifiedEvents,
}: Omit<MatchAccuracyProps, "type" | "children">) => {
  const { t } = useTenantTranslation();
  const formatPercent = useFormatNumber()(1, true);
  const fp = totalLabellerEvents - matchedEvents;
  const fn = totalVerifiedEvents - matchedEvents;
  const grandTotal = matchedEvents + fp + fn;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto auto auto",
        gap: "1px",
        "& > *": { px: 0.5, py: 0.25, whiteSpace: "nowrap" },
      }}
    >
      <Box />
      <Typography
        variant="caption"
        sx={{ fontWeight: "bold", textAlign: "center" }}
      >
        {t("actualEvent")}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: "bold", textAlign: "center" }}
      >
        {t("noActualEvent")}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
        {t("labellerEvent")}
      </Typography>
      <Typography
        variant="caption"
        sx={{ textAlign: "center", fontWeight: "bold", color: "success.main" }}
      >
        {formatCell(matchedEvents, grandTotal, formatPercent)}
      </Typography>
      <Typography
        variant="caption"
        sx={{ textAlign: "center", color: "warning.main" }}
      >
        {formatCell(fp, grandTotal, formatPercent)}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
        {t("noLabellerEvent")}
      </Typography>
      <Typography
        variant="caption"
        sx={{ textAlign: "center", color: "error.main" }}
      >
        {formatCell(fn, grandTotal, formatPercent)}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: "center" }}>
        —
      </Typography>
    </Box>
  );
};

const ConfusionMatrixContent = ({
  entries,
  formatLabel = identityFormatLabel,
  buildCellHref,
}: Omit<ConfusionMatrixProps, "type" | "children">) => {
  const { t } = useTenantTranslation();
  const formatPercent = useFormatNumber()(1, true);

  const { predictedLabels, actualLabels, countMap, actualTotals } =
    useMemo(() => {
      const { predicted, actual, counts, totals } = entries.reduce(
        (acc, entry) => {
          acc.predicted.add(entry.predicted);
          acc.actual.add(entry.actual);
          acc.counts.set(`${entry.predicted}|${entry.actual}`, entry.count);
          acc.totals.set(
            entry.actual,
            (acc.totals.get(entry.actual) ?? 0) + entry.count
          );
          return acc;
        },
        {
          predicted: new Set<string>(),
          actual: new Set<string>(),
          counts: new Map<string, number>(),
          totals: new Map<string, number>(),
        }
      );
      return {
        predictedLabels: [...predicted].sort(),
        actualLabels: [...actual].sort(),
        countMap: counts,
        actualTotals: totals,
      };
    }, [entries]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `auto ${actualLabels.map(() => "auto").join(" ")}`,
        gap: "1px",
        "& > *": { px: 0.5, py: 0.25, whiteSpace: "nowrap" },
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
        {t("predicted")} \ {t("actual")}
      </Typography>
      {actualLabels.map((label) => (
        <Typography
          key={label}
          variant="caption"
          sx={{ fontWeight: "bold", textAlign: "center" }}
        >
          {formatLabel(label)}
        </Typography>
      ))}
      {predictedLabels.map((predicted) => (
        <Box key={predicted} sx={{ display: "contents" }}>
          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
            {formatLabel(predicted)}
          </Typography>
          {actualLabels.map((actual) => {
            const count = countMap.get(`${predicted}|${actual}`) ?? 0;
            const total = actualTotals.get(actual) ?? 0;
            const isErrorRate = predicted !== actual;
            return (
              <Typography
                key={actual}
                component={Link}
                to={buildCellHref(predicted, actual)}
                variant="caption"
                sx={{
                  textAlign: "center",
                  fontWeight: isErrorRate ? "normal" : "bold",
                  color: getCellColor({
                    isErrorRate,
                    percent: (count / total) * 100,
                    background: "dark",
                  }),
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {formatCell(count, total, formatPercent)}
              </Typography>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export const AccuracyTooltip = ({ children, ...rest }: Props) => {
  const myId = useId();
  const { pinnedId, setPinnedId } = usePinnedTooltip();
  const pinned = pinnedId === myId;
  const anotherPinned = pinnedId !== null && pinnedId !== myId;
  const [hoverOpen, setHoverOpen] = useState(false);
  const isPinnable = rest.type === "confusion_matrix";

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openHover = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHoverOpen(true);
  };
  const closeHoverWithDelay = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setHoverOpen(false);
      closeTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      setPinnedId((current) => (current === myId ? null : current));
    };
  }, [myId, setPinnedId]);

  const togglePinned = () => {
    setPinnedId(pinned ? null : myId);
  };

  const unpin = () => {
    if (pinned) setPinnedId(null);
  };

  useEffect(() => {
    if (!pinned) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinnedId(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [pinned, setPinnedId]);

  const content =
    rest.type === "match" ? (
      <MatchAccuracyContent
        matchedEvents={rest.matchedEvents}
        totalLabellerEvents={rest.totalLabellerEvents}
        totalVerifiedEvents={rest.totalVerifiedEvents}
      />
    ) : (
      <ConfusionMatrixContent
        entries={rest.entries}
        formatLabel={rest.formatLabel}
        buildCellHref={rest.buildCellHref}
      />
    );

  if (rest.type === "confusion_matrix" && rest.entries.length === 0) {
    return children;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
        gap: 0.5,
      }}
    >
      {children}
      <ClickAwayListener
        onClickAway={unpin}
        mouseEvent={pinned ? "onClick" : false}
      >
        <Tooltip
          open={pinned || (hoverOpen && !anotherPinned)}
          title={content}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: { maxWidth: "none" },
              onMouseEnter: openHover,
              onMouseLeave: closeHoverWithDelay,
            },
          }}
        >
          <Box
            component="span"
            onMouseEnter={openHover}
            onMouseLeave={closeHoverWithDelay}
            onClick={() => {
              if (!isPinnable) return;
              togglePinned();
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0.5,
              borderRadius: "50%",
              cursor: isPinnable ? "pointer" : "help",
              color: pinned ? "primary.main" : "action.disabled",
              "&:hover": {
                backgroundColor: "action.hover",
                color: pinned ? "primary.main" : "action.active",
              },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      </ClickAwayListener>
    </Box>
  );
};
